import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAdminCompanies } from "@/lib/api/companies.queries";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { Table, THead, TH, TBody, TR, TD, EmptyState } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { CertStatusBadge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";

export function CompaniesList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const query = useAdminCompanies({
    q: search || undefined,
    status: statusFilter || undefined,
    page,
    pageSize,
  });

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  const total = query.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Empresas</h1>
        <p className="text-sm text-slate-500 mt-1">
          Todas las empresas registradas en la plataforma.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <form onSubmit={onSearch} className="flex items-center gap-2 flex-1 min-w-0 max-w-md">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por NIT o razón social"
              className="pl-8"
            />
          </div>
          <Button variant="outline" size="md" type="submit">
            Buscar
          </Button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="suspended">Suspendido</option>
        </select>
      </div>

      <Card className="overflow-hidden">
        {query.isPending ? (
          <CenteredSpinner label="Cargando empresas…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : (query.data?.companies ?? []).length === 0 ? (
          <EmptyState
            title={search ? "Sin resultados" : "No hay empresas"}
            description={search ? "Intenta con otro NIT o razón social." : "No se encontraron empresas."}
          />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>NIT-DV</TH>
                  <TH>Razón social</TH>
                  <TH>Equipo</TH>
                  <TH>Onboarding</TH>
                  <TH>Ambiente DIAN</TH>
                  <TH>Cert</TH>
                  <TH>Creada</TH>
                </tr>
              </THead>
              <TBody>
                {query.data.companies.map((c) => (
                  <TR
                    key={c.nit}
                    className="cursor-pointer"
                    onClick={() => navigate(`/companies/${c.nit}`)}
                  >
                    <TD className="font-mono text-xs">
                      {c.nit}{c.dv ? `-${c.dv}` : ""}
                    </TD>
                    <TD>
                      <p className="font-medium text-slate-900">{c.legalName}</p>
                      {c.tradeName && (
                        <p className="text-[11px] text-slate-500">{c.tradeName}</p>
                      )}
                    </TD>
                    <TD className="text-xs text-slate-600">{c.teamName ?? c.teamId}</TD>
                    <TD>
                      <span className="text-xs capitalize">{c.onboardingStatus}</span>
                    </TD>
                    <TD>
                      <span className={cn(
                        "text-xs font-medium px-1.5 py-0.5 rounded",
                        c.dianEnvironment === "prod" ? "bg-red-50 text-red-700" : "bg-sky-50 text-sky-700"
                      )}>
                        {c.dianEnvironment}
                      </span>
                    </TD>
                    <TD>
                      <CertStatusBadge status={c.certStatus ?? "missing"} />
                    </TD>
                    <TD className="text-[11px] text-slate-500">{formatDate(c.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <p className="text-xs text-slate-600">
                {total} total · página {page} de {pageCount}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
